import { BridgeClient } from './BridgeClient';

const ONBOARDING_STATE_REQUEST = 'onboarding.state';
const APP_BOOTSTRAP_REQUEST = 'app.bootstrap';
const LIVE_SET_ENABLED_REQUEST = 'live.set_enabled';

const REASON_NONE = 'none';
const REASON_KILL_SWITCH = 'kill_switch';

function normalizeLiveStateFromOnboarding(raw) {
  const live = raw && raw.state && raw.state.live;
  if (!live || typeof live !== 'object') return null;
  return {
    enabled: live.enabled === true,
    reason: typeof live.reason === 'string' ? live.reason : REASON_NONE,
  };
}

function normalizeLiveStateFromBootstrap(raw) {
  const ui = raw && raw.ui;
  if (!ui || typeof ui !== 'object') return null;
  if (typeof ui.live_enabled !== 'boolean') return null;
  return {
    enabled: ui.live_enabled,
    reason: ui.live_enabled ? REASON_NONE : REASON_KILL_SWITCH,
  };
}

function normalizeLiveStateFromWrite(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const live = raw.live;
  if (!live || typeof live !== 'object') return null;
  return {
    enabled: live.enabled === true,
    reason: typeof live.reason === 'string' ? live.reason : REASON_NONE,
  };
}

/**
 * Read the current live state from the plugin bridge.
 *
 * Order of attempts:
 *   1. `onboarding.state` — rich shape with `state.live.{enabled, reason}` and
 *      `capabilities.live_enabled_v1`. This is the primary read.
 *   2. `app.bootstrap` — legacy shape with `ui.live_enabled` and
 *      `capabilities.live_enabled_v1`. Used only as a temporary fallback when
 *      the plugin does not yet expose `state.live` (e.g. older builds).
 *
 * @returns {Promise<{enabled: boolean, reason: string, capability: boolean, source: string}>}
 */
export async function getLiveState() {
  const client = new BridgeClient();

  try {
    const raw = await client.sendMessageWithResponse(ONBOARDING_STATE_REQUEST, {});
    const capabilities = (raw && raw.state && raw.state.capabilities) || {};
    const live = normalizeLiveStateFromOnboarding(raw);
    if (live) {
      return {
        ...live,
        capability: capabilities.live_enabled_v1 === true,
        source: ONBOARDING_STATE_REQUEST,
      };
    }
  } catch (err) {
    // fall through to bootstrap
  }

  const raw = await client.sendMessageWithResponse(APP_BOOTSTRAP_REQUEST, {});
  const capabilities = (raw && raw.capabilities) || {};
  const live = normalizeLiveStateFromBootstrap(raw);
  if (!live) {
    return {
      enabled: false,
      reason: REASON_KILL_SWITCH,
      capability: false,
      source: APP_BOOTSTRAP_REQUEST,
    };
  }
  return {
    ...live,
    capability: capabilities.live_enabled_v1 === true,
    source: APP_BOOTSTRAP_REQUEST,
  };
}

/**
 * Flip the live kill-switch on the plugin side.
 *
 * Sends `live.set_enabled` with a real boolean payload and returns the
 * normalized live state from the response.
 *
 * @param {boolean} enabled
 * @returns {Promise<{enabled: boolean, reason: string}>}
 * @throws Error when the bridge returns `{ result: 'error', code: 'INVALID_PAYLOAD', error }`
 */
export async function setLiveEnabled(enabled) {
  if (typeof enabled !== 'boolean') {
    throw new Error("'enabled' must be a boolean");
  }
  const client = new BridgeClient();
  const raw = await client.sendMessageWithResponse(LIVE_SET_ENABLED_REQUEST, {
    enabled,
  });
  if (raw && raw.result === 'error') {
    const err = new Error(raw.error || 'bridge_error');
    err.code = raw.code || 'BRIDGE_ERROR';
    throw err;
  }
  const live = normalizeLiveStateFromWrite(raw);
  if (!live) {
    throw new Error('Invalid bridge response: missing live object');
  }
  return live;
}

const bridgeKillswitchApi = {
  getLiveState,
  setLiveEnabled,
};

export default bridgeKillswitchApi;
